// Accounting › Close a month (Blueprint's alone; pages/ux-full accounting/close), on ui-web's ListPage
// (patterns/README.md, BL-189): last month's checks (periods.closeChecks), each with the part of Books
// that fixes it, closed after "I reviewed the aging and the accruals" (periods.close) exactly as Ledger's
// Close does; the months beside it (periods.list), with the owner's Reopen behind its reason
// (periods.reopen), as Ledger's Months has it.
import { useState } from 'react';
import { CloseChecklist, ConfirmDialog, monthRows, RowList } from '@flowology/ui-web/fl';
import { monthWords, type CloseChecksProps } from '@flowology/ui-web';
import { ListPage } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { officeHost } from '../blueprint/host.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { REOPEN_PERMS, reopenable, reopenWarning, type Period } from './corrections-model.ts';
import { monthName, previousMonth, seesPayroll, todayIn } from './model.ts';
import { listStatus } from './money-list-state.ts';
import { FIX_PART } from './money-lists-model.ts';
import { BOOKS_PARTS } from './parts.ts';
import type { BooksPart } from './props.ts';
import { errorText, useAct, useQuery } from './ui.tsx';

const host = officeHost(() => undefined);
const PART = BOOKS_PARTS.find((p) => p.part === 'close')!;
const closedOn = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(new Date(iso));

export function CloseMonthPage({
  nav,
  held,
  toast,
  go,
  timeZone,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  /** Opens the part of Books that fixes a check. */
  go: (part: BooksPart) => void;
  timeZone?: string;
}) {
  // last month, as Ledger's Close checks it
  const [, end] = previousMonth(todayIn(timeZone));
  const c = useQuery<CloseChecksProps>('periods.closeChecks', { date: end });
  const months = useQuery<{ periods: Period[] }>('periods.list', {});
  // a month closed or reopened: the checks and the months read again
  const act = useAct(toast, async () => {
    await Promise.all([c.reload(), months.reload()]);
  });
  const [reviewed, setReviewed] = useState(false);
  const [reopening, setReopening] = useState<Period | null>(null);
  const payroll = seesPayroll(held);
  const mayReopen = holdsAny(held, REOPEN_PERMS);
  const latest = months.data ? reopenable(months.data.periods) : null;
  const crumb = usePageCrumb(nav);
  const reopen = async (reason: string) => {
    if (!reopening) return;
    try {
      await client.call('periods.reopen', { date: reopening.startDate, reason: reason.trim() });
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
    toast(`${monthName(reopening.startDate)} is open again. Close it when you’re done.`);
    setReopening(null);
    await Promise.all([c.reload(), months.reload()]);
  };
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{ ...(crumb ? { crumb } : {}), title: PART.bp ?? PART.rail, lede: PART.lede }}
          list={{
            title: c.data ? `Close ${monthWords(c.data.period.startDate)}` : 'Close last month',
            what: 'the month-end checks',
            status: listStatus(c.data, c.error),
            failedText: c.error ?? 'The month-end checks didn’t load.',
            onRetry: () => void c.reload(),
            children: c.data ? (
              <CloseChecklist
                data={c.data}
                host={host}
                timeZone={timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
                reviewed={reviewed}
                onReviewed={setReviewed}
                busy={act.busy}
                onFix={(key) => {
                  const part = FIX_PART[key];
                  if (part && (part !== 'payroll' || payroll)) go(part);
                }}
                onClose={() =>
                  void act.run(
                    'periods.close',
                    { date: end, reviewedAgingAndAccruals: true },
                    `${monthWords(c.data!.period.startDate).split(' ')[0]} is closed.`,
                  )
                }
              />
            ) : null,
          }}
          aside={{
            title: 'Months',
            what: 'the months',
            status: months.data?.periods.length === 0 ? 'empty' : listStatus(months.data, months.error),
            emptyText: 'Nothing posted yet, so no months to show.',
            failedText: months.error ?? 'The months didn’t load.',
            onRetry: () => void months.reload(),
            children: months.data ? (
              <RowList
                rows={monthRows(
                  months.data.periods.map((p) => ({
                    id: p.id,
                    month: monthName(p.startDate),
                    closed: p.status === 'closed',
                    ...(p.closedAt ? { closedOn: closedOn(p.closedAt) } : {}),
                  })),
                  mayReopen && latest ? { id: latest.id, onPress: () => setReopening(latest) } : null,
                )}
                {...(!mayReopen && latest ? { note: 'Only the owner reopens a closed month.' } : {})}
              />
            ) : null,
          }}
          layers={
            <ConfirmDialog
              isOpen={!!reopening}
              onClose={() => setReopening(null)}
              title={reopening ? `Reopen ${monthName(reopening.startDate)}?` : 'Reopen'}
              confirmLabel={reopening ? `Reopen ${monthName(reopening.startDate)}` : 'Reopen'}
              reason={{ label: 'Why', missing: 'Say why it’s reopened.' }}
              onConfirm={reopen}
            >
              {reopening ? reopenWarning(monthName(reopening.startDate)) : null}
            </ConfirmDialog>
          }
        />
      </main>
    </div>
  );
}
